import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import axios from '../utils/api';
import '../styles/Settings.css';
import '../styles/DashboardHome.css';

const PERIODS = [
  { value: 'day', label: 'Last 24 hours' },
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
  { value: 'year', label: 'Last 12 months' },
  { value: 'lifetime', label: 'Lifetime' },
];

const formatCost = (n) => {
  const value = Number(n) || 0;
  if (value < 0.01 && value > 0) return `$${value.toFixed(6)}`;
  return `$${value.toFixed(2)}`;
};

const formatTokens = (n) => Number(n || 0).toLocaleString();

const UsageStats = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [period, setPeriod] = useState('month');
  const [userId, setUserId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'AI Usage';
    if (user?.role !== 'master_admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [user?.role, navigate]);

  useEffect(() => {
    if (user?.role !== 'master_admin') return;
    const fetchStats = async () => {
      setLoading(true);
      setError('');
      try {
        const params = { period };
        if (userId) params.user_id = userId;
        const res = await axios.get('/auth/usage-stats/', { params });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load usage stats');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [period, userId, user?.role]);

  if (user?.role !== 'master_admin') return null;

  const totals = data?.totals || {};
  const lifetime = data?.lifetime || {};

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>AI Usage & Cost</h1>
        <p>Track OpenAI token usage and estimated spend across the platform</p>
      </div>

      {error && (
        <Alert variant="danger" className="settings-alert">{error}</Alert>
      )}

      <div className="settings-section">
        <div className="section-header">
          <i className="fas fa-filter"></i>
          <h2>Filters</h2>
        </div>
        <div className="settings-form" style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
          <div className="form-group" style={{ minWidth: 200, flex: 1 }}>
            <label htmlFor="period">Period</label>
            <select
              id="period"
              className="form-control"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIODS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ minWidth: 240, flex: 1 }}>
            <label htmlFor="user_id">Staff / Admin</label>
            <select
              id="user_id"
              className="form-control"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">All users</option>
              {(data?.users || []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name || u.email} ({u.role === 'master_admin' ? 'Admin' : 'Staff'})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 24 }}>
        {[
          { label: 'Period Tokens', value: loading ? '…' : formatTokens(totals.total_tokens), icon: 'fa-microchip' },
          { label: 'Period Cost', value: loading ? '…' : formatCost(totals.estimated_cost_usd), icon: 'fa-dollar-sign' },
          { label: 'Period Requests', value: loading ? '…' : formatTokens(totals.requests), icon: 'fa-bolt' },
          { label: 'Lifetime Cost', value: loading ? '…' : formatCost(lifetime.estimated_cost_usd), icon: 'fa-chart-line' },
        ].map((stat) => (
          <div key={stat.label} className="stat-card">
            <div className="stat-icon">
              <i className={`fas ${stat.icon}`}></i>
            </div>
            <div className="stat-content">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="settings-section">
        <div className="section-header">
          <i className="fas fa-infinity"></i>
          <h2>Lifetime Totals</h2>
        </div>
        <p style={{ color: '#64748b', marginBottom: 12 }}>
          Prompt: <strong>{formatTokens(lifetime.prompt_tokens)}</strong>
          {' · '}
          Completion: <strong>{formatTokens(lifetime.completion_tokens)}</strong>
          {' · '}
          Total: <strong>{formatTokens(lifetime.total_tokens)}</strong>
          {' · '}
          Cost: <strong>{formatCost(lifetime.estimated_cost_usd)}</strong>
          {' · '}
          Requests: <strong>{formatTokens(lifetime.requests)}</strong>
        </p>
      </div>

      <div className="settings-section">
        <div className="section-header">
          <i className="fas fa-users"></i>
          <h2>Usage by User ({PERIODS.find((p) => p.value === period)?.label})</h2>
        </div>
        {loading ? (
          <p>Loading…</p>
        ) : !(data?.by_user || []).length ? (
          <p style={{ color: '#64748b' }}>No usage recorded for this filter.</p>
        ) : (
          <div className="reports-table-container" style={{ overflowX: 'auto' }}>
            <table className="reports-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 10 }}>User</th>
                  <th style={{ textAlign: 'left', padding: 10 }}>Role</th>
                  <th style={{ textAlign: 'right', padding: 10 }}>Tokens</th>
                  <th style={{ textAlign: 'right', padding: 10 }}>Requests</th>
                  <th style={{ textAlign: 'right', padding: 10 }}>Est. Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.by_user.map((row) => (
                  <tr key={row.user_id || row.email}>
                    <td style={{ padding: 10 }}>
                      <div>{row.full_name}</div>
                      <small style={{ color: '#64748b' }}>{row.email}</small>
                    </td>
                    <td style={{ padding: 10 }}>{row.role === 'master_admin' ? 'Admin' : 'Staff'}</td>
                    <td style={{ padding: 10, textAlign: 'right' }}>{formatTokens(row.total_tokens)}</td>
                    <td style={{ padding: 10, textAlign: 'right' }}>{formatTokens(row.requests)}</td>
                    <td style={{ padding: 10, textAlign: 'right' }}>{formatCost(row.estimated_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="settings-section">
        <div className="section-header">
          <i className="fas fa-calendar-day"></i>
          <h2>Daily Breakdown</h2>
        </div>
        {loading ? (
          <p>Loading…</p>
        ) : !(data?.daily || []).length ? (
          <p style={{ color: '#64748b' }}>No daily data for this period.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="reports-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 10 }}>Day</th>
                  <th style={{ textAlign: 'right', padding: 10 }}>Tokens</th>
                  <th style={{ textAlign: 'right', padding: 10 }}>Requests</th>
                  <th style={{ textAlign: 'right', padding: 10 }}>Est. Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.daily.map((row) => (
                  <tr key={row.day}>
                    <td style={{ padding: 10 }}>{row.day}</td>
                    <td style={{ padding: 10, textAlign: 'right' }}>{formatTokens(row.total_tokens)}</td>
                    <td style={{ padding: 10, textAlign: 'right' }}>{formatTokens(row.requests)}</td>
                    <td style={{ padding: 10, textAlign: 'right' }}>{formatCost(row.estimated_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageStats;
